// src/pages/PurchasePaymentsModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert } from '@mui/material';
import api from '../api/index.js';
import { format } from 'date-fns';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700, // Ancho ajustado
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto'
};

const PurchasePaymentsModal = ({ open, onClose, purchaseId, onUpdate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Estado para el nuevo abono
    const [newPayment, setNewPayment] = useState({
        amount: '',
        payment_date: format(new Date(), 'yyyy-MM-dd')
    });

    const fetchPayments = async () => {
        if (!purchaseId) return;
        setLoading(true);
        try {
            const response = await api.get(`/purchases/${purchaseId}/payments`);
            setData(response.data);
        } catch (err) { setError('No se pudo cargar el historial de pagos.'); } 
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (open) {
            fetchPayments();
        }
    }, [open, purchaseId]);

    const handleAddPayment = async () => {
        try {
            await api.post(`/purchases/${purchaseId}/payments`, newPayment);
            fetchPayments(); // Recargar historial de pagos
            onUpdate(); // Notificar a la página principal para que recargue la lista de compras
            setNewPayment({ amount: '', payment_date: format(new Date(), 'yyyy-MM-dd') });
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar el abono.');
        }
    };

    const totalPaid = data?.payments.reduce((sum, p) => sum + Number(p.amount), 0) || 0;
    const pendingBalance = (data?.purchase.total_amount || 0) - totalPaid;

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                {loading ? <CircularProgress /> : error ? <Alert severity="error">{error}</Alert> : data && (
                    <>
                        <Typography variant="h6">Pagos de Factura: {data.purchase.invoice_number}</Typography>
                        <Typography>Proveedor: {data.purchase.supplier_name}</Typography>
                        <Typography>Total Factura: ${Number(data.purchase.total_amount).toFixed(2)}</Typography>
                        <Typography>Total Pagado: ${totalPaid.toFixed(2)}</Typography>
                        <Typography variant="h6" color={pendingBalance > 0 ? 'error.main' : 'success.main'}>
                            Saldo Pendiente: ${pendingBalance.toFixed(2)}
                        </Typography>
                        
                        <Box sx={{ my: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                label="Monto del Abono" type="number"
                                value={newPayment.amount} onChange={e => setNewPayment({...newPayment, amount: e.target.value})}
                            />
                            <TextField
                                label="Fecha de Pago" type="date"
                                value={newPayment.payment_date} onChange={e => setNewPayment({...newPayment, payment_date: e.target.value})}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button variant="contained" onClick={handleAddPayment}>Añadir Pago</Button>
                        </Box>

                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table size="small">
                                <TableHead><TableRow><TableCell>Fecha de Pago</TableCell><TableCell align="right">Monto</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {data.payments.map(p => (
                                        <TableRow key={p.id}><TableCell>{p.payment_date}</TableCell><TableCell align="right">${Number(p.amount).toFixed(2)}</TableCell></TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default PurchasePaymentsModal;